<template>
  <div id="item-profile-off-chain-offers">
    <div v-if="data.length" ref="scrollComponent" class="table-container">
      <table class="table">
        <thead>
          <tr class="table-header-row">
            <th class="table-header-cell">Price</th>
            <th class="table-header-cell">USD Price</th>
            <th class="table-header-cell">From</th>
          </tr>
        </thead>
        <tbody>
          <tr :ref="setRowRef" v-for="item in data" :key="item.id" class="table-row">
            <td class="table-cell">
              <CryptoPrice :amount="item.price" :currency="item.cryptoCurrency"/>
            </td>
            <td class="table-cell">
              ${{ item.usdPrice }}
            </td>
            <td class="table-cell">
              <router-link
                :to="{ name: 'user-profile', params: { userNickname: item.userNickname }}"
                class="link"
              >
                {{ item.userName }}
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="!isOffersLoading && !data.length" class="empty-list">
      No offers yet.
    </p>
    <ContentLoader :loading="isOffersLoading"/>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUpdate,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
} from 'vue';
import throttle from 'lodash/throttle';

import { useStore } from '@/store';
import { Modules } from '@/store/props';
import { Actions } from '@/store/modules/item-profile/props';

import PaginationOptions from '@/shared/models/pagination-options';

import CryptoPrice from '@/components/CryptoPrice.vue';
// import ExpansionCard from '@/components/ExpansionCard.vue';
import ContentLoader from '@/components/ContentLoader.vue';

export default defineComponent({
  name: 'ItemProfileBidList',

  components: {
    CryptoPrice,
    // ExpansionCard,
    ContentLoader,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { id } = toRefs(props);
    const scrollComponent = ref<HTMLElement | null>(null);
    const isOffersLoading = ref<boolean>(false);

    const store = useStore();
    const data = computed(() => store.state.ItemProfile.offChainOffers.data);
    const pagination = computed(() => store.state.ItemProfile.offChainOffers.pagination);
    const isLastPage = computed(() => store.state.ItemProfile.offChainOffers.isLastPage);
    const total = computed(() => store.state.ItemProfile.offChainOffers.total);

    let rowRefs: HTMLTableRowElement[] = [];

    const loadOffChainOffers = (options: Partial<PaginationOptions> & { id: string }) => {
      isOffersLoading.value = true;
      store.dispatch(`${Modules.ItemProfile}/${Actions.fetchOffChainOffers}`, { ...options })
        .finally(() => {
          isOffersLoading.value = false;
        });
    };

    const setRowRef = (el: HTMLTableRowElement) => {
      if (el) {
        rowRefs.push(el);
      }
    };

    const checkElementsBounds = () => {
      if (!isLastPage.value && !isOffersLoading.value && scrollComponent.value) {
        const element = rowRefs[rowRefs.length - 1];
        if (element && (element.getBoundingClientRect().top
          < scrollComponent.value.getBoundingClientRect().bottom)
        ) {
          loadOffChainOffers({ id: id.value, page: pagination.value.page + 1 });
        }
      }
    };

    onBeforeUpdate(() => {
      rowRefs = [];
    });

    onMounted(() => {
      loadOffChainOffers({ id: id.value, page: 1 });

      if (scrollComponent.value) {
        scrollComponent.value.addEventListener('scroll', throttle(checkElementsBounds, 800, { leading: false }));
      }
    });

    onUnmounted(() => {
      if (scrollComponent.value) {
        scrollComponent.value.removeEventListener('scroll', throttle(checkElementsBounds, 800, { leading: false }));
      }
    });

    return {
      isOffersLoading,
      data,
      total,
      scrollComponent,
      setRowRef,
    };
  },
});
</script>

<style lang="scss" scoped>
@import 'src/styles/mixins';

.table-container {
  @include table-container-common;
  max-height: 300px;
  overflow: visible;
}

.empty-list {
  @include typo-caption;
}
</style>
